import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { DeleteZoneRequestDTO } from '@/core/api/dtos/request/zone/delete'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDeleteZone } from '@/core/hooks/useZone'
import { zoneDeleted } from '@/core/store/slices/zones'
import type { DeleteZoneModalFormData } from '@/features/zones/modals/DeleteZoneModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseZoneDeletionProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
	redirectPath?: string // Path to redirect to after successful deletion
	redirectTimeout?: number // Timeout to redirect to after successful deletion
}

export const useZoneDeletion = (props?: UseZoneDeletionProps) => {
	const { onSuccess, onError, redirectPath, redirectTimeout = 0 } = props || {}
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useDeleteZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				if (selectedZone?.id) {
					// Dispatch action to Redux
					dispatch(zoneDeleted(selectedZone.id))

					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.zone.delete.success.title'),
						description: t('entities.zone.delete.success.description', {
							name: selectedZone.name,
						}),
					})

					// Close modal
					setIsModalVisible(false)
					setSelectedZone(null)

					// Redirect if a path was provided
					if (redirectPath) {
						setTimeout(() => {
							navigate(redirectPath)
						}, redirectTimeout)
					}

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				}
			},
			onError: (error: Error) => {
				// Show error notification
				showNotification({
					type: 'error',
					message: t('entities.zone.delete.error.title'),
					description: t('entities.zone.delete.error.description', {
						name: selectedZone?.name,
					}),
				})

				// Call onError callback if provided
				if (onError) onError(error)
			},
		},
	})

	const openDeleteModal = (zone: Zone) => {
		setSelectedZone(zone)
		setIsModalVisible(true)
	}

	const closeDeleteModal = () => {
		setIsModalVisible(false)
		setSelectedZone(null)
	}

	const handleDeleteZone = (data: DeleteZoneModalFormData) => {
		if (selectedZone?.id) {
			// `password` is collected only for subscription downgrade flow.
			// `/my/zones/{id}` expects only `DeleteZoneRequestDTO` (no password).
			const deleteZonePayload: DeleteZoneRequestDTO = {
				reason: data.reason,
			}

			mutate({
				zoneId: selectedZone.id,
				data: deleteZonePayload,
			})
		}
	}

	return {
		selectedZone,
		isModalVisible,
		isDeleting: isPending,
		openDeleteModal,
		closeDeleteModal,
		handleDeleteZone,
	}
}

export default useZoneDeletion
